// SPDX-License-Identifier: LicenseRef-Proprietary

import React from 'react';
import type { PubmedPaperSummary } from '../../../../../services/pubmedService';
import type { AbstractTriageBrief } from '../../../lib/models';
import type { AnalysisProgress } from '../hooks/useAnalysisProgress';
import { categorizePapers } from '../types/resultCategorization';
import { rankByTitle } from '../lib/rankByTitle';
import { PaperListItem } from './PaperListItem';
import { ProgressIndicator } from './ProgressIndicator';
import { BinarySectionResults } from './BinarySectionResults';
import { MIN_SEARCH_QUERY_LENGTH } from '../constants';
import './ProgressIndicator.css';

export interface SearchTabProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  isSearching: boolean;
  searchError: string | null;
  searchResults: PubmedPaperSummary[];
  onPaperClick: (pmid: string) => void;
  // AI Triage Props
  triageBriefs: Record<string, AbstractTriageBrief>;
  triageErrors: Record<string, { code: string; message: string }>;
  triageStatus: 'idle' | 'loading' | 'cancelled' | 'done' | 'error';
  failedPmids: string[];
  onBatchTriage: (pmids?: string[]) => Promise<void>;
  onRetryFailed: () => void;
  selectedInputSource: string | null;
  // NEW: Selection Props
  selectedPmids?: string[];
  onSelectionChange?: (pmid: string, selected: boolean) => void;
  onSelectAll?: () => void;
  onSelectNone?: () => void;
  // NEW: Progress Props
  analysisProgress?: AnalysisProgress | null;
  completionSummary?: React.ReactNode;
}

export const SearchTab: React.FC<SearchTabProps> = ({
  searchQuery,
  setSearchQuery,
  isSearching,
  searchError,
  searchResults,
  onPaperClick,
  triageBriefs,
  triageErrors,
  triageStatus,
  failedPmids,
  onBatchTriage,
  onRetryFailed,
  selectedInputSource,
  // NEW: Selection Props
  selectedPmids = [],
  onSelectionChange,
  onSelectAll,
  onSelectNone,
  // NEW: Progress Props
  analysisProgress,
  completionSummary,
}) => {
  const analyzedCount = Object.keys(triageBriefs).length;
  // Calculate distinct error count (excluding those that succeeded in a retry)
  const distinctErrorPmids = Object.keys(triageErrors).filter(pmid => !triageBriefs[pmid]);
  const errorCount = distinctErrorPmids.length;

  const hasSelection = selectedPmids.length > 0;
  const selectionMode = searchResults.length > 0;

  // Rank results by title relevance (papers with search terms in title appear first)
  const rankedResults = React.useMemo(
    () => rankByTitle(searchResults, searchQuery),
    [searchResults, searchQuery]
  );

  // NEW: Categorize results for display
  const categorizedResults = categorizePapers(rankedResults, triageBriefs, triageErrors);
  const showCategorizedView = analyzedCount > 0 || errorCount > 0;

  // NEW: Binary section state management
  const [expandedBinarySections, setExpandedBinarySections] = React.useState<Set<string>>(
    new Set(['relevant'])
  );

  // NEW: Paper expansion state management
  const [expandedPapers, setExpandedPapers] = React.useState<Set<string>>(new Set());

  const handleBinarySectionToggle = (section: string) => {
    setExpandedBinarySections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  const handlePaperExpansionChange = (pmid: string, expanded: boolean) => {
    setExpandedPapers(prev => {
      const newSet = new Set(prev);
      if (expanded) {
        newSet.add(pmid);
      } else {
        newSet.delete(pmid);
      }
      return newSet;
    });
  };

  const handleAnalyzeSelected = () => {
    if (hasSelection) {
      onBatchTriage(selectedPmids);
    } else {
      onBatchTriage(); // Fallback to original behavior
    }
  };

  return (
    <div className="evidence-drawer__search-container">
      <input
        type="text"
        className="evidence-drawer__search-input"
        placeholder="Search PubMed..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        aria-label="PubMed search query"
      />
      
      {!isSearching && !searchError && rankedResults.length > 0 && (
        <>
          {/* Selection Controls with Primary CTA on same row */}
          <div className="evidence-drawer__selection-controls">
            <div className="evidence-drawer__selection-actions">
              <button
                className="evidence-drawer__selection-button"
                onClick={onSelectAll}
                disabled={triageStatus === 'loading'}
              >
                Select All
              </button>
              <button
                className="evidence-drawer__selection-button"
                onClick={onSelectNone}
                disabled={triageStatus === 'loading'}
              >
                Select None
              </button>
            </div>
            <div className="evidence-drawer__selection-row">
              <button
                className={`evidence-drawer__batch-button ${triageStatus === 'loading' ? 'evidence-drawer__batch-button--loading' : ''}`}
                onClick={handleAnalyzeSelected}
                disabled={triageStatus === 'loading' || (!hasSelection && selectionMode)}
              >
                {triageStatus === 'loading' ? (
                  <>
                    <div className="evidence-drawer__batch-spinner" />
                    {(() => {
                      // useAnalysisProgress exposes total / completed / failed and an
                      // estimatedTimeRemaining (ms) computed from a rolling average of
                      // per-paper completion times — so we can show "X of N" + an ETA.
                      const total =
                        analysisProgress?.total ??
                        (hasSelection
                          ? selectedPmids.length
                          : Math.min(rankedResults.length, 10));
                      const done =
                        (analysisProgress?.completed ?? 0) +
                        (analysisProgress?.failed ?? 0);
                      const current = Math.min(done + 1, total);
                      const etaMs = analysisProgress?.estimatedTimeRemaining;
                      const etaSeconds =
                        typeof etaMs === 'number' && etaMs > 0
                          ? Math.max(1, Math.round(etaMs / 1000))
                          : null;
                      return (
                        <>
                          Analyzing paper {current} of {total}
                          {etaSeconds != null ? ` — ~${etaSeconds}s remaining` : '…'}
                        </>
                      );
                    })()}
                  </>
                ) : (
                  hasSelection
                    ? `Analyze ${selectedPmids.length} selected papers with AI`
                    : 'Analyze with AI'
                )}
              </button>
            </div>
          </div>

          {/* Progress Indicator - Show during analysis */}
          {analysisProgress && triageStatus === 'loading' && (
            <ProgressIndicator progress={analysisProgress} />
          )}

          {/* Completion Feedback - Show after analysis completes */}
          {completionSummary}

          <div className="evidence-drawer__batch-controls">
            {/* Batch button moved to selection row above */}

            {!hasSelection && selectionMode && triageStatus === 'idle' && (
              <div className="evidence-drawer__batch-helper-text">
                Select papers to analyze with AI (max 10 papers)
              </div>
            )}

            {hasSelection && selectedPmids.length > 10 && triageStatus === 'idle' && (
              <div className="evidence-drawer__batch-warning">
                Note: Analyzing {selectedPmids.length} papers may take several minutes
              </div>
            )}

            {failedPmids.length > 0 && triageStatus === 'done' && (
              <button 
                className="evidence-drawer__batch-button evidence-drawer__batch-button--retry"
                onClick={onRetryFailed}
              >
                Retry Failed ({failedPmids.length} papers)
              </button>
            )}

            {triageStatus === 'error' && (
              <div className="evidence-drawer__error evidence-drawer__batch-error">
                Batch triage failed. Please try again.
              </div>
            )}
          </div>
        </>
      )}

      {isSearching && (
        <div className="evidence-drawer__loading">Searching...</div>
      )}
      {searchError && (
        <div className="evidence-drawer__error">{searchError}</div>
      )}
      {!isSearching && !searchError && searchQuery.trim().length > 0 && searchQuery.trim().length < MIN_SEARCH_QUERY_LENGTH && (
        <div className="evidence-drawer__hint">Type at least {MIN_SEARCH_QUERY_LENGTH} characters to search</div>
      )}
      {!isSearching && !searchError && rankedResults.length > 0 && (
        <>
          {/* Show categorized results if we have analyzed papers, otherwise show regular list */}
          {showCategorizedView ? (
            <BinarySectionResults
              categorizedResults={categorizedResults}
              onPaperClick={onPaperClick}
              selectedInputSource={selectedInputSource}
              selectedPmids={selectedPmids}
              onSelectionChange={onSelectionChange}
              expandedSections={expandedBinarySections}
              onSectionToggle={handleBinarySectionToggle}
              expandedPapers={expandedPapers}
              onPaperExpansionChange={handlePaperExpansionChange}
            />
          ) : (
            <div className="evidence-drawer__results">
              {rankedResults.map((paper, index) => {
                const triageBrief = triageBriefs[paper.pmid];
                
                // If we have a filter active, check if this paper matches
                const isHiddenByFilter = selectedInputSource && 
                  (!triageBrief || !triageBrief.potentialInputSources.includes(selectedInputSource as any));

                if (isHiddenByFilter) return null;

                return (
                  <PaperListItem
                    key={paper.pmid}
                    paper={paper}
                    onClick={() => onPaperClick(paper.pmid)}
                    triageBrief={triageBrief}
                    triageError={triageErrors[paper.pmid]}
                    isLoading={triageStatus === 'loading' && !triageBriefs[paper.pmid] && !triageErrors[paper.pmid] && (hasSelection ? selectedPmids.includes(paper.pmid) : index < 10)}
                    highlightInputSource={selectedInputSource}
                    // NEW: Selection props
                    selectionMode={selectionMode}
                    isSelected={selectedPmids.includes(paper.pmid)}
                    onSelectionChange={onSelectionChange}
                    // NEW: Expansion props
                    isExpanded={expandedPapers.has(paper.pmid)}
                    onExpansionChange={handlePaperExpansionChange}
                  />
                );
              })}
            </div>
          )}
        </>
      )}
      {!isSearching && !searchError && searchQuery.trim().length >= MIN_SEARCH_QUERY_LENGTH && searchResults.length === 0 && (
        <div className="evidence-drawer__empty">No matching papers — try a broader search query</div>
      )}
    </div>
  );
};
